import * as actionTypes from "../constants/actionTypes";
import * as config from "../constants/config";
import * as modelHelper from "../helpers/modelHelper";

import PosForm from "../models/gui/PosForm";

let initialState = {
    initialized: false,
    loading: 0,
    lastDispensedList: [],
    favoritesList: [],
    productList: [],
    linearProductList: [],
    simpleProductList: [],
    fandeckList: [],
    customFandeckList: [],
    formulaList: [],
    formulaCount: 0,
    colorantList: [],
    allColorantList: [],
    allMarginList: [],
    marginList: [],
    customMarginList: [],
    userList: [],
    unitList: [],
    tintometricSystemList: [],
    posInfo: new PosForm(null),
    serverPlugins: [],
    supportedImportDataFormats: [],
    activeUser: null,
    userGroupsList: [],
    protocolList: [],
    inFormatList: [],
    outFormatList: [],
    disabledProducts: 0,
    disabledFandecks: 0,
    tintometricSystem: null,
    matchingInitialized: null,
    matchingAvailable: null,
    matchingResultCode: null,
    matchingResultMessage: null,
    matchingProductList: [],
    partialUpdates: null,
    partialUpdatesCount: null,
    partialUpdatesPreparing: [],
    databasesReadyForPartialUpdates: [],
    tmcAvailable: false,
    fileTransferInProgress: false,
    fileTransferData: {filesRemaining: 0, percFinished: 0}
};

/**
 * Structure of global store:
 * - initialized {Boolean}              - if true, process of client initialization is done
 * - loading {Number}                   - initial loading progress of global initial requests
 * - lastDispensedList {Array}          - array of objects consist of product, fandeck, color and base objects
 * - favoritesList {Array}              - tree structure of formula objects and folders
 * - productList {Array}                - tree structure of product objects and product groups
 * - linearProductList {Array}          - array of objects for rendering of product list with product groups
 * - simpleProductList {Array}          - array of product objects with no product groups
 * - fandeckList {Array}                - array with fandeck objects
 * - customFandeckList {Array}          - array with custom fandeck objects
 * - formulaList {Array}                - array with formula objects for global search
 * - formulaCount {Number}              - number of formulas found in global search
 * - colorantList {Array}               - array of colorant objects
 * - allColorantList {Array}            - array of colorant objects from all databases
 * - allMarginList {Array}              - array of default and custom margins
 * - marginList {Array}                 - array of default margins
 * - customMarginList {Array}           - array of custom margins
 * - userList {Array}                   - array with users
 * - unitList {Array}                   - array of all units
 * - tintometricSystemList {Array}      - array of all tintometric systems
 * - posInfo {Object}
 * - serverPlugins {Array}
 * - supportedImportDataFormats {Array} - array with supported data formats
 * - protocolList {Array}               - array of supported file transfer protocols
 * - inFormatList {Array}               - array of supported file transfer in formats
 * - outFormatList {Array}              - array of supported file transfer out formats
 * - disabledProducts {Number}
 * - disabledFandecks {Number}
 * - tintometricSystem {Object | null}
 * - matchingInitialized {Boolean | null}
 * - matchingAvailable {Boolean | null}
 * - matchingResultCode {Number | String | null}
 * - matchingResultMessage {String}
 * - matchingProductList {Array}
 * - partialUpdates {Object}
 * - partialUpdatesCount {Number | null}
 * - databasesReadyForPartialUpdates {Array}
 * - tmcAvailable {Boolean}
 */
export const globalReducer = (state = initialState, action) => {
    switch (action.type) {
        case actionTypes.FILE_TRANSFER_STARTED:
            return {
                ...state,
                fileTransferInProgress: true
            };
        case actionTypes.FILE_TRANSFER_FILE_AVAILABLE:
            return {
                ...state,
                fileTransferData: {
                    ...state.fileTransferData,
                    filesRemaining: action.payload.filesRemaining
                }
            };
        case actionTypes.FILE_TRANSFER_PROCEEDED:
            return {
                ...state,
                fileTransferInProgress: true,
                fileTransferData: {...state.fileTransferData, percFinished: action.payload.percFinished * 100}
            };
        case actionTypes.FILE_TRANSFER_FINISHED:
            return {
                ...state,
                fileTransferInProgress: initialState.fileTransferInProgress,
                fileTransferData: {...state.fileTransferData, percFinished: initialState.fileTransferData.percFinished}
            };
        case actionTypes.SOFTWARE_INITIALIZED:
            return {
                ...state,
                initialized: true
            };
        case actionTypes.SOFTWARE_START_INITIALIZATION:
        case actionTypes.SOFTWARE_NOT_INITIALIZED:
        case actionTypes.CLOSE_SERVER_CONNECTION:
            return {
                ...state,
                initialized: false,
                loading: 0
            };
        case actionTypes.LOG_OUT_SUCCESS:
            return {
                ...initialState,
                initialized: true,
                loading: 0
            };
        case actionTypes.INCREASE_LOADING:
            return {
                ...state,
                loading: state.loading + 1
            };
        case actionTypes.RCC_INITIALIZED:
            return {
                ...state,
                loading: state.loading + config.RCC_INIT
            };
        case actionTypes.DRIVERS_INITIALIZED:
            return {
                ...state,
                loading: state.loading + config.DRIVERS_INIT
            };
        case actionTypes.MACHINE_INITIALIZED:
            return {
                ...state,
                loading: state.loading + config.MACHINE_INIT
            };
        case actionTypes.PRINTER_INITIALIZED:
            return {
                ...state,
                loading: state.loading + config.PRINTER_INIT
            };
        case actionTypes.SPECTRO_INITIALIZED:
            return {
                ...state,
                loading: state.loading + config.SPECTRO_INIT
            };
        case actionTypes.SAVE_LAST_DISPENSED_FORMULA_LIST:
            return {
                ...state,
                lastDispensedList: modelHelper.mapLastDispensedFormulasToObjects(action.payload.lastDispensedList)
            };
        case actionTypes.SAVE_FAVORITE_FORMULA_LIST:
            return {
                ...state,
                favoritesList: modelHelper.mapFavoritesToObjects(action.payload.favoritesList)
            };
        case actionTypes.SAVE_COLORANT_LIST:
            return {
                ...state,
                colorantList: modelHelper.mapColorantsToObjects(action.payload.colorantList)
            };
        case actionTypes.SAVE_ALL_COLORANT_LIST:
            return {
                ...state,
                allColorantList: modelHelper.mapColorantsToObjects(action.payload.colorantList)
            };
        case actionTypes.SAVE_PRODUCT_LIST:
            let productList = modelHelper.mapProductsToObjects(action.payload.productList);

            return {
                ...state,
                productList: productList,
                linearProductList: modelHelper.processTreeToLinearList(productList, "product").list
            };
        case actionTypes.SAVE_PRODUCT_SIMPLE_LIST:
            return {
                ...state,
                simpleProductList: modelHelper.mapProductsToSimpleList(action.payload.productList)
            };
        case actionTypes.SAVE_FANDECK_LIST:
            return {
                ...state,
                fandeckList: modelHelper.mapFandecksToObjects(action.payload.fandeckList)
            };
        case actionTypes.SAVE_FORMULA_LIST:
            let globalList = modelHelper.mapColorCombinationsToObjects(
                action.payload.customGlobalSearch
                    ? action.payload.formulaList.filter((elem, index, array) => {
                          for (var i = 0; i < index; i++) {
                              if (JSON.stringify(array[i]) === JSON.stringify(elem)) return false;
                          }
                          return true;
                      })
                    : action.payload.formulaList
            );

            return {
                ...state,
                formulaCount: action.payload.formulaList.length === 0 ? action.payload.formulaCount : globalList.length,
                formulaList: globalList
            };
        case actionTypes.RELOAD_FORMULA_LIST:
            return {
                ...state,
                formulaList: state.formulaList.concat(
                    modelHelper.mapColorCombinationsToObjects(action.payload.formulaList)
                )
            };
        case actionTypes.RESET_FORMULA_LIST:
            return {
                ...state,
                formulaCount: 0,
                formulaList: []
            };
        case actionTypes.SAVE_CUSTOM_FANDECK_LIST:
            return {
                ...state,
                customFandeckList: modelHelper.mapFandecksToObjects(action.payload.customFandeckList)
            };
        case actionTypes.SAVE_MARGIN_LIST:
            let marginList = modelHelper.mapMarginsToObjects(action.payload.marginList.default);
            let customMarginList = modelHelper.mapMarginsToObjects(action.payload.marginList.custom);

            return {
                ...state,
                allMarginList: marginList.concat(customMarginList),
                marginList: marginList,
                customMarginList: customMarginList
            };
        case actionTypes.SAVE_USER_LIST:
            return {
                ...state,
                userList: modelHelper.mapUsersToObjects(action.payload.userList)
            };
        case actionTypes.SAVE_ACTIVE_USER:
            return {
                ...state,
                activeUser: {
                    userId: action.payload.userId,
                    isLocal: action.payload.isLocal
                }
            };
        case actionTypes.SAVE_SELECTED_USER_GROUPS:
            let newUserList = modelHelper.updateUserList(
                state.activeUser.userId,
                state.activeUser.isLocal,
                state.userList,
                action.payload.userGroupsList
            );
            return {
                ...state,
                userList: newUserList
            };
        case actionTypes.SAVE_USER_GROUPS_LIST:
            return {
                ...state,
                userGroupsList: modelHelper.mapUserGroupsToObjects(action.payload.userGroupsList)
            };
        case actionTypes.SAVE_LAST_DISPENSED_FORMULAS_WITH_OFFSET:
            return {
                ...state,
                lastDispensedList: state.lastDispensedList.concat(
                    modelHelper.mapLastDispensedFormulasToObjects(action.payload.lastDispensedList)
                )
            };
        case actionTypes.SAVE_UNIT_LIST:
            let unitList = modelHelper.mapUnitsToObjects(action.payload.unitList);

            window.storeProvider.setUnits(unitList);

            return {
                ...state,
                unitList: unitList
            };
        case actionTypes.SAVE_TINTOMETRIC_SYSTEM_LIST:
            return {
                ...state,
                tintometricSystemList: modelHelper.mapTintometricSystemToObjects(action.payload.tintometricSystemList)
            };
        case actionTypes.SAVE_POS_FORM_INFO:
            return {
                ...state,
                posInfo: modelHelper.mapPosToObject(action.payload.posInfo)
            };
        case actionTypes.CUSTOM_DATA_SAVE_SUPPORTED_IMPORT_FORMATS:
            return {
                ...state,
                supportedImportDataFormats: modelHelper.mapFormatDataImportToObjects(
                    action.payload.supportedDataFormats
                )
            };
        case actionTypes.CUSTOM_DATA_UPDATE_IMPORT_FORMAT:
            let format,
                formats = [];

            for (format of state.supportedImportDataFormats) {
                if (action.payload.format && format.code === action.payload.format.code) {
                    formats.push(action.payload.format);
                } else {
                    formats.push(format.updateActive(false));
                }
            }

            return {
                ...state,
                supportedImportDataFormats: formats
            };
        case actionTypes.SAVE_SERVER_PLUGINS:
            return {
                ...state,
                serverPlugins: action.payload.plugins
            };
        case actionTypes.SAVE_FILE_TRANSFER_PROTOCOL_LIST:
            return {
                ...state,
                protocolList: action.payload.protocolList
            };
        case actionTypes.SAVE_FILE_TRANSFER_IN_FORMATS:
            return {
                ...state,
                inFormatList: action.payload.inFormatList
            };
        case actionTypes.SAVE_FILE_TRANSFER_OUT_FORMATS:
            return {
                ...state,
                outFormatList: action.payload.outFormatList
            };
        case actionTypes.SAVE_DISABLED_FANDECKS:
            return {
                ...state,
                disabledFandecks: action.payload.disabledFandecks
            };
        case actionTypes.SAVE_DISABLED_PRODUCTS:
            return {
                ...state,
                disabledProducts: action.payload.disabledProducts
            };
        case actionTypes.SAVE_TINTOMETRIC_SYSTEM:
            return {
                ...state,
                tintometricSystem: modelHelper.mapTintometricSystemToObject(action.payload.tintometricSystem)
            };
        case actionTypes.MATCHING_INITIALIZED:
            if (action.payload.initialized === true) {
                return {
                    ...state,
                    matchingInitialized: true
                };
            }

            return {
                ...state,
                matchingInitialized: false,
                matchingAvailable: false
            };
        case actionTypes.MATCHING_AVAILABLE:
            return {
                ...state,
                matchingAvailable: action.payload.available
            };
        case actionTypes.MATCHING_SAVE_PRODUCT_LIST:
            return {
                ...state,
                matchingResultCode: action.payload.resultCode,
                matchingResultMessage: action.payload.resultMessage,
                matchingProductList: action.payload.productList
            };
        case actionTypes.WEBSOCKET_CLOSED:
        case actionTypes.DATABASE_CHANGE:
            return initialState;
        case actionTypes.DATABASE_CHANGE_GAMMA:
            return {
                ...state,
                productList: [],
                linearProductList: [],
                simpleProductList: [],
                fandeckList: [],
                customFandeckList: [],
                formulaList: [],
                formulaCount: 0,
                colorantList: []
            };
        case actionTypes.PARTIAL_UPDATES_SAVE_DATA:
            return {
                ...state,
                partialUpdates: action.payload.data
            };
        case actionTypes.SET_PARTIAL_UPDATES_PREPARING:
            if (action.payload.value && !state.partialUpdatesPreparing.includes(action.payload.databaseId)) {
                return {
                    ...state,
                    partialUpdatesPreparing: [action.payload.databaseId, ...state.partialUpdatesPreparing]
                };
            }

            return {
                ...state,
                partialUpdatesPreparing: state.partialUpdatesPreparing.filter(
                    (databaseId) => databaseId !== action.payload.databaseId
                )
            };
        case actionTypes.PARTIAL_UPDATES_CLEAN_ALL:
            return {
                ...state,
                partialUpdates: null,
                partialUpdatesCount: null
            };
        case actionTypes.PARTIAL_UPDATES_SAVE_COUNT:
            return {
                ...state,
                partialUpdatesCount: action.payload.data
            };
        case actionTypes.SAVE_DATABASES_READY_FOR_PARTIAL_UPDATES:
            return {
                ...state,
                databasesReadyForPartialUpdates: action.payload.databaseList
            };
        case actionTypes.TMC_AVAILABLE:
            return {
                ...state,
                tmcAvailable: action.payload.available
            };
        default:
            return state;
    }
};
